import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Progress component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 226:2086):
 * - Sizes: Small (4px), Medium (6px)
 * - Track: rgba(18,18,18,0.05) bg, full rounded
 * - Fill: Grey-950 (#09090B), full rounded
 * - Optional: Label text, percentage display
 */
const progressVariants = cva(
  'relative w-full overflow-hidden rounded-full bg-black/5 dark:bg-white/10',
  {
    variants: {
      size: {
        sm: 'h-1',
        default: 'h-1.5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const Progress = React.forwardRef(
  ({ className, value = 0, size, label, showPercentage = false, ...props }, ref) => {
    const percentage = Math.min(100, Math.max(0, value));

    if (label || showPercentage) {
      return (
        <div className="flex items-center gap-3 w-full">
          {label && (
            <span className={cn(
              'shrink-0 font-medium text-black dark:text-white text-right',
              size === 'sm' ? 'text-xs' : 'text-sm'
            )}>
              {label}
            </span>
          )}
          <ProgressPrimitive.Root
            ref={ref}
            className={cn(progressVariants({ size }), 'flex-1', className)}
            {...props}
          >
            <ProgressPrimitive.Indicator
              className="h-full rounded-full bg-grey-950 transition-all duration-300 ease-out dark:bg-grey-50"
              style={{ width: `${percentage}%` }}
            />
          </ProgressPrimitive.Root>
          {showPercentage && (
            <span className={cn(
              'shrink-0 font-medium text-black dark:text-white',
              size === 'sm' ? 'text-xs' : 'text-sm'
            )}>
              {percentage}%
            </span>
          )}
        </div>
      );
    }

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(progressVariants({ size }), className)}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className="h-full rounded-full bg-grey-950 transition-all duration-300 ease-out dark:bg-grey-50"
          style={{ width: `${percentage}%` }}
        />
      </ProgressPrimitive.Root>
    );
  }
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress, progressVariants };
